import { z } from 'zod';

/**
 * Contratto condiviso (api-client FE ↔ route BE) per la query string delle
 * liste telephony. Un solo posto decide quali parametri esistono: così un
 * parametro sbagliato non viene più ignorato in silenzio ma o è nel contratto
 * (e il BE lo applica) o viene rifiutato con 400 al boundary Zod.
 *
 * Nome canonico del filtro testuale: `q`. `search` sopravvive SOLO come alias
 * deprecato, ripiegato su `q` da `withDeprecatedSearchAlias`; il suo uso è
 * contato dal BE (`telephony_list_deprecated_search_alias_total{surface}`).
 *
 * Piano di rimozione dell'alias: quando il contatore resta a zero in
 * produzione per un intero ciclo di osservazione (vedi
 * docs/runbooks/telephony-list-query-contract.md) si toglie `search` dai
 * campi, si lascia lo schema `.strict()` così un client ritardatario riceve
 * 400 «Campi non riconosciuti: search» invece di una lista non filtrata.
 */
export const TELEPHONY_LIST_SEARCH_PARAM = 'q';
/** @deprecated usare {@link TELEPHONY_LIST_SEARCH_PARAM} */
export const TELEPHONY_LIST_DEPRECATED_SEARCH_ALIAS = 'search';

export const telephonyListSearchTermSchema = z.string().trim().max(100).optional();

export const telephonyListPaginationFields = {
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).default(50),
};

export const telephonyListSearchFields = {
  [TELEPHONY_LIST_SEARCH_PARAM]: telephonyListSearchTermSchema,
  /** @deprecated usare `q` */
  [TELEPHONY_LIST_DEPRECATED_SEARCH_ALIAS]: telephonyListSearchTermSchema,
};

/**
 * Ripiega l'alias deprecato `search` su `q` e lo toglie dall'output: le route
 * vedono un solo nome. `q` vince se presenti entrambi. Stringa vuota → undefined.
 */
type SearchAliasInput = { q?: string | undefined; search?: string | undefined };
export type WithCanonicalSearch<Output extends SearchAliasInput> = Omit<Output, 'search'> & { q: string | undefined };

export function withDeprecatedSearchAlias<Schema extends z.ZodTypeAny & { _output: SearchAliasInput }>(
  object: Schema,
): z.ZodEffects<Schema, WithCanonicalSearch<z.output<Schema>>, z.input<Schema>> {
  return object.transform((parsed: z.output<Schema>): WithCanonicalSearch<z.output<Schema>> => {
    const { search, ...rest } = parsed;
    // Il cast serve solo per TS: a runtime è lo stesso oggetto senza `search`
    // e con `q` normalizzato (stringa vuota → undefined).
    return { ...rest, q: rest.q || search || undefined } as WithCanonicalSearch<z.output<Schema>>;
  });
}

/** True se la query grezza (prima della validazione) usa l'alias deprecato. */
export function usesDeprecatedSearchAlias(rawQuery: unknown): boolean {
  if (!rawQuery || typeof rawQuery !== 'object') return false;
  return (rawQuery as Record<string, unknown>)[TELEPHONY_LIST_DEPRECATED_SEARCH_ALIAS] !== undefined;
}

/** Rimuove l'alias deprecato dal tipo di input offerto all'api-client FE. */
export type WithoutDeprecatedSearchAlias<Input> = Omit<Input, typeof TELEPHONY_LIST_DEPRECATED_SEARCH_ALIAS>;

/**
 * Liste VoIP senza filtro lato server (censimento 2026-10-30): trunk SIP,
 * menu IVR, gruppi di squillo, code, sale conferenza, caselle vocali. Il BE
 * restituisce sempre l'elenco completo del tenant; `.strict()` fa sì che
 * `?search=`, `?q=`, `?page=` ecc. producano 400 invece di una lista non
 * filtrata spacciata per risultato. Quando una di queste liste acquisirà un
 * filtro reale, lo schema va esteso QUI (e l'api-client FE segue il tipo).
 */
export const voipEntityListQuerySchema = z.object({}).strict();
export type VoipEntityListQuery = z.infer<typeof voipEntityListQuerySchema>;

/** Superfici che espongono una lista telephony con filtro `q` (label metrica). */
export const TELEPHONY_SEARCHABLE_LIST_SURFACES = ['sip-extensions', 'extensions'] as const;
export type TelephonySearchableListSurface = typeof TELEPHONY_SEARCHABLE_LIST_SURFACES[number];
